import { LoadingButton } from '@atlaskit/button';
import { ApolloQueryResult } from '@apollo/client';
import Form, { Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import {
  ErrorComponents,
  InfoPanel,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { resendInvoice, updateInvoiceWithMemo } from '@atlassian/temp-proxies';
import { BillingDocWithLineItems, InvoiceWithLineItems, UpdateInvoiceRequest } from '@atlassian/cssxp-types-billing';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import * as React from 'react';

interface FormData {
  memo?: string;
}

interface Props {
  withInputMemoFieldFlag: boolean;
  billingDoc?: BillingDocWithLineItems;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

const InvoiceUpdatePane = ({ withInputMemoFieldFlag = true, billingDoc, refetch }: Props): JSX.Element | null => {
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceRequest, any>({
    submitFormAction: withInputMemoFieldFlag ? updateInvoiceWithMemo : resendInvoice,
    onComplete: () => {
      closeSidebar();
      if (refetch) {
        refetch();
      }
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { closeSidebar } = useSidebar();

  const MEMO_LENGTH_LIMIT = 500;
  const invoice = billingDoc as InvoiceWithLineItems;
  const transactionAccountId = invoice?.transactionAccountId ?? '';

  if (!invoice) {
    return null;
  }

  if (invoice?.sourceSystem === SourceSystem.HAMS) {
    return null;
  }

  const requestInvoiceUpdateData = (formData: FormData) => {
    const requestData: UpdateInvoiceRequest = {
      memo: (formData.memo ?? '').slice(0, MEMO_LENGTH_LIMIT),
      invoiceId: invoice.id,
      transactionAccountId,
    };

    return requestData;
  };

  const handleFormSubmit = (formData: FormData) => {
    const requestData = requestInvoiceUpdateData(formData);
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-invoice-success')
      .withMessage('Successfully updated and resent invoice.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);

    closeSidebar();
  }

  return (
    <div className="invoice-update-container" style={{ maxWidth: '500px' }} data-testId="invoice-update-container">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={withInputMemoFieldFlag ? 'Add memo (optional)' : 'Resend invoice'}></FormHeader>
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the invoice for the following reason"
                      message={formError}
                    />
                  }
                />
              ) : !transactionAccountId ? (
                <InfoPanel
                  type="warning"
                  title={withInputMemoFieldFlag ? 'Cannot add memo' : 'Cannot resend invoice'}
                  message={'There is no transaction account id on this invoice'}
                />
              ) : (
                <InfoPanel
                  type="information"
                  title="Invoice will be sent to:"
                  message={''}
                  detailList={[
                    () => (
                      <ul>
                        <li>All the billing admins.</li>
                        <li>All the recipients in invoice group.</li>
                      </ul>
                    ),
                  ]}
                />
              )}
            </FormSection>
            {withInputMemoFieldFlag && (
              <FormSection>
                <Field<string, HTMLTextAreaElement> name="memo" label="">
                  {({ fieldProps }) => (
                    <TextArea
                      {...fieldProps}
                      placeholder="Add memo (Max limit: 500 characters)"
                      name="memo"
                      defaultValue={invoice?.memo}
                      testId={'update-memo-field'}
                    />
                  )}
                </Field>
              </FormSection>
            )}

            <FormFooter align="start">
              <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} data-testId="update-invoice">
                {withInputMemoFieldFlag ? 'Resend with memo' : 'Resend'}
              </LoadingButton>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default React.memo(InvoiceUpdatePane);
